import React, { useCallback, useContext, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { ChartSettingsSidebar } from './ChartSettingsSidebar';
import { ChartImageView, ChartPreviewContainer } from './styles';
import { fetchChart, getChartPayload } from './chartHelper';
import { PageElementsContext } from '../../../../context/pageElementsContext';
import { ChartSettingsToolbar } from './chartSettingsToolbar';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../../context/complexDocumentContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../../../context/complexArtboardContext';
import { deepCopy } from '../../../../utils/javascript';
import { barChartTypes } from './chartConstants';
import { message } from 'antd';
import { CHART_TRANSLATION } from './translationValues';

const NAMESPACES = ['CHART_TRANSLATION'];

const ChartContainer = ({ element, selected, t, creativeBlock }) => {
  const { contents, uuid, style } = element;
  const {
    dataUrl,
    dataSource,
    dataRequest,
    labelVariable,
    valueVariable,
    descVariable,
    seriesNameVariable,
    seriesValueVariable,
    colorPalette,
    seriesList,
  } = contents;
  const { isVariable, height } = style;

  const repeatBlockVariableName =
    creativeBlock?.variableContract?.length && creativeBlock?.variableContract[0].type === 'object'
      ? creativeBlock?.variableContract[0].name
      : undefined;

  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `chart-${element.uuid}`);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [isPreview, updateArtboard] = useArtboardContext(previewModeSelector, `chart-${element.uuid}`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN] || store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ChartContainer`);

  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(artboardSelector);

  const { updateComplexElementDetails } = useContext(PageElementsContext);

  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const updateChartDetails = async copiedContent => {
    const payload = getChartPayload(copiedContent, isVariable);
    try {
      const data = await fetchChart(payload);
      copiedContent.dataUrl = `data:image/png;base64, ${data.base64ImageBytes}`;
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(uuid, copiedContent, 'contents');
    } catch (e) {
      message.error(
        t(CHART_TRANSLATION.CHART_UPDATE_ERROR.key, CHART_TRANSLATION.CHART_UPDATE_ERROR.value),
      );
    }
  };

  const getImageData = async () => {
    if (!dataUrl) {
      const copiedContent = deepCopy(contents);
      updateChartDetails(copiedContent);
    }
    const baseVariable = dataRequest?.baseVariable;
    const repeatVariable = dataRequest?.repeatVariable;
    if (
      isPreview &&
      baseVariable &&
      dataSet &&
      (dataSet[baseVariable] || dataSet[repeatVariable])
    ) {
      let repeatDataset = dataSet;
      const repeatIndex = creativeBlock.dataIndex;
      if (repeatVariable && dataSet[repeatVariable]) {
        repeatDataset = dataSet[repeatVariable][repeatIndex];
      }
      const copiedContent = deepCopy(contents);

      // remove dataUrl from static chart contents before preview for dynamic chart
      copiedContent.dataUrl = '';
      updateComplexElementDetails(uuid, copiedContent, 'contents');

      if (barChartTypes.includes(contents.type)) {
        const labelKey = globalVariables.find(gVar => gVar.id === labelVariable)?.placeHolderText;
        const sNameKey = globalVariables.find(gVar => gVar.id === seriesNameVariable)
          ?.placeHolderText;
        const sValueKey = globalVariables.find(gVar => gVar.id === seriesValueVariable)
          ?.placeHolderText;
        const previewColorPalette = [...seriesList.map(({ color }) => color), ...colorPalette];
        const previewLabelsSet = new Set();
        const previewNameSet = new Set();
        repeatDataset[baseVariable].forEach(item => {
          previewLabelsSet.add(item[labelKey]);
          previewNameSet.add(item[sNameKey]);
        });
        const previewLabels = Array.from(previewLabelsSet);
        const previewNameList = Array.from(previewNameSet);
        const previewNameMap = new Map();
        previewNameList.forEach(name => {
          previewNameMap.set(name, []);
          previewLabels.forEach(label => {
            const val = repeatDataset[baseVariable].find(
              item => item[labelKey] === label && item[sNameKey] === name,
            );
            const value = val && val[sValueKey] ? val[sValueKey] : '';
            const tempArr = previewNameMap.get(name);
            tempArr.push(value);
            previewNameMap.set(name, tempArr);
          });
        });

        // logic to add colors to chart on preview when not enough colors available in color palette.
        let colorIndex = 0;
        let colorDivideNum = 1;
        const previewSeriesList = previewNameList.map(name => {
          if (!previewColorPalette[colorIndex]) {
            colorIndex = 0;
            colorDivideNum *= 2;
          }
          const color = previewColorPalette[colorIndex];
          const colorAfterDivision = color.map(num => Math.floor(num / colorDivideNum));
          colorIndex += 1;
          return {
            name: name,
            series: previewNameMap.get(name),
            color: colorAfterDivision,
          };
        });
        copiedContent.seriesList = previewSeriesList;
        copiedContent.labels = previewLabels;
      } else {
        const labelKey = globalVariables.find(gVar => gVar.id === labelVariable)?.placeHolderText;
        const valueKey = globalVariables.find(gVar => gVar.id === valueVariable)?.placeHolderText;
        const descKey = globalVariables.find(gVar => gVar.id === descVariable)?.placeHolderText;
        const previewColorPalette = [...dataSource.map(({ color }) => color), ...colorPalette];
        // logic to add colors to chart on preview when not enough colors available in color palette.
        let colorIndex = 0;
        let colorDivideNum = 1;
        copiedContent.dataSource = repeatDataset[baseVariable].map(v => {
          if (!previewColorPalette[colorIndex]) {
            colorIndex = 0;
            colorDivideNum *= 2;
          }
          const color = previewColorPalette[colorIndex];
          const colorAfterDivision = color.map(num => Math.floor(num / colorDivideNum));
          const retObj = {
            label: v[labelKey],
            value: v[valueKey],
            description: v[descKey],
            color: colorAfterDivision,
          };
          colorIndex += 1;
          return retObj;
        });
      }
      updateChartDetails(copiedContent);
    }
  };

  useEffect(() => {
    getImageData();
  }, []);

  useEffect(() => {
    if (repeatBlockVariableName !== contents.repeatVariable && !isInteractiveDesign) {
      const copiedContent = deepCopy(contents);
      copiedContent.repeatVariable = repeatBlockVariableName;
      const payload = getChartPayload(copiedContent, isVariable);
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(uuid, copiedContent, 'contents');
    }
  }, [repeatBlockVariableName]);

  let component = dataUrl ? <ChartImageView src={`${dataUrl}`} /> : <></>;
  if (!dataUrl && isPreview) {
    component = (
      <ChartPreviewContainer style={{ height: `${height}px` }}>
        <span>
          {t(
            CHART_TRANSLATION.CHART_PREVIEW_GENERATE.key,
            CHART_TRANSLATION.CHART_PREVIEW_GENERATE.value,
          )}
        </span>
      </ChartPreviewContainer>
    );
  }

  return (
    <>
      {component}
      {!!selected && (
        <>
          <ChartSettingsSidebar
            element={element}
            t={t}
            globalVariables={globalVariables}
            updateComplexElementDetails={updateComplexElementDetails}
            isInteractiveDesign={isInteractiveDesign}
          />
          <ChartSettingsToolbar
            element={element}
            t={t}
            updateComplexElementDetails={updateComplexElementDetails}
          />
        </>
      )}
    </>
  );
};

const Chart = withTranslation(NAMESPACES)(ChartContainer);

export { Chart };
